/**
 * Auth Recovery Utilities
 *
 * Utilities for recovering from stuck authentication states after
 * backend caching changes or token mismatches.
 */
import { purgeApolloPersistedCache } from '../apollo/cachePersistence';

/**
 * Clear all authentication state from browser storage.
 *
 * This is useful when:
 * - Backend caching method changes (e.g., token hashing)
 * - User gets stuck unable to login or logout
 * - Token mismatches between client and server cache
 *
 * Note: This will force the user to re-authenticate.
 */
export function clearAllAuthState(): void {
  console.warn('[Auth Recovery] Clearing all authentication state');

  // Clear all localStorage
  localStorage.clear();

  // Clear all sessionStorage
  sessionStorage.clear();

  // Clear service worker cache if present
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }

  // Clear the persisted (IndexedDB) Apollo cache so no tenant-scoped data
  // survives a recovery/reset.
  void purgeApolloPersistedCache();

  console.warn('[Auth Recovery] All auth state cleared. Reloading...');
}

/**
 * Check if auth state might be stale.
 * Returns true if state seems problematic.
 */
export function checkAuthStateHealth(): boolean {
  const tenantId = localStorage.getItem('selectedTenantId');
  const hasSessionTenant = !!sessionStorage.getItem('selectedTenantId');

  // Check for inconsistencies
  if (tenantId && !hasSessionTenant) {
    console.warn('[Auth Health] Tenant in localStorage but not sessionStorage');
    return false;
  }

  return true;
}

// Make available in console for debugging
if (typeof window !== 'undefined') {
  (
    window as Window & {
      __authRecovery?: {
        clearAllAuthState: () => void;
        checkAuthStateHealth: () => boolean;
      };
    }
  ).__authRecovery = {
    clearAllAuthState,
    checkAuthStateHealth,
  };
}
